import { useMemo } from "react";
import { Select } from "./select";

export type UserFilterSelectUser = {
  id: string;
  name: string;
};

type Option = {
  key: string;
  value: string;
  label: string;
};

export type UserFilterSelectProps = {
  users: UserFilterSelectUser[];
  currentUserId?: string;
  currentUserName?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  includeAllOption?: boolean;
  allValue?: string;
  allLabel: string;
  loading?: boolean;
  loadingLabel: string;
  emptyLabel: string;
  currentUserFallbackLabel: string;
  meLabel: string;
  className?: string;
  ariaLabel?: string;
  id?: string;
};

const buildOptions = ({
  users,
  currentUserId,
  currentUserName,
  includeAllOption,
  allValue,
  allLabel,
  currentUserFallbackLabel,
  meLabel,
}: {
  users: UserFilterSelectUser[];
  currentUserId?: string;
  currentUserName?: string;
  includeAllOption: boolean;
  allValue: string;
  allLabel: string;
  currentUserFallbackLabel: string;
  meLabel: string;
}): Option[] => {
  const sortedUsers = [...users].sort((first, second) =>
    first.name.localeCompare(second.name),
  );
  const options: Option[] = [];

  if (includeAllOption) {
    options.push({ key: "all-option", value: allValue, label: allLabel });
  }

  if (currentUserId) {
    const loggedUser = sortedUsers.find((member) => member.id === currentUserId);

    if (loggedUser) {
      options.push({
        key: `user-${loggedUser.id}`,
        value: loggedUser.id,
        label: `${loggedUser.name} ${meLabel}`,
      });
    } else {
      options.push({
        key: `user-${currentUserId}`,
        value: currentUserId,
        label: `${currentUserName ?? currentUserFallbackLabel} ${meLabel}`,
      });
    }
  }

  sortedUsers
    .filter((member) => member.id !== currentUserId)
    .forEach((member) => {
      options.push({
        key: `user-${member.id}`,
        value: member.id,
        label: member.name,
      });
    });

  return options;
};

export function UserFilterSelect({
  users,
  currentUserId,
  currentUserName,
  value,
  onChange,
  disabled = false,
  includeAllOption = true,
  allValue = "",
  allLabel,
  loading = false,
  loadingLabel,
  emptyLabel,
  currentUserFallbackLabel,
  meLabel,
  className,
  ariaLabel,
  id,
}: UserFilterSelectProps) {
  const options = useMemo(
    () =>
      buildOptions({
        users,
        currentUserId,
        currentUserName,
        includeAllOption,
        allValue,
        allLabel,
        currentUserFallbackLabel,
        meLabel,
      }),
    [
      allLabel,
      allValue,
      currentUserFallbackLabel,
      currentUserId,
      currentUserName,
      includeAllOption,
      meLabel,
      users,
    ],
  );

  const selectedOptionLabel = useMemo(
    () => options.find((option) => option.value === value)?.label,
    [options, value],
  );

  if (loading) {
    return (
      <Select
        id={id}
        value={value}
        onChange={(event) => event.preventDefault()}
        disabled
        title={loadingLabel}
        className={className}
        aria-label={ariaLabel}
      >
        <option value={value}>{loadingLabel}</option>
      </Select>
    );
  }

  if (users.length === 0) {
    return (
      <Select
        id={id}
        value={value}
        onChange={(event) => event.preventDefault()}
        disabled
        title={emptyLabel}
        className={className}
        aria-label={ariaLabel}
      >
        <option value={value || currentUserId || allValue}>{emptyLabel}</option>
      </Select>
    );
  }

  return (
    <Select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      title={selectedOptionLabel}
      className={className}
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <option key={option.key} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  );
}

export default UserFilterSelect;
